/**
 * チャットでやり取りされた画像一覧を再取得するための処理
 *
 * - debounce: 受信直後の即時再取得を避け、サーバ側反映待ちのため少し遅らせる
 * - retry: 取得失敗時に一定回数リトライする
 * - stale guard: in-flight 中に画面遷移などで reservationId が変わった場合、古い予約の結果で更新しない
 *
 */
type TimerId = ReturnType<typeof setTimeout>;

type ValueOrGetter<T> = T | (() => T);

const getValue = <T>(valueOrGetter: ValueOrGetter<T>): T => {
  return typeof valueOrGetter === "function" ? (valueOrGetter as () => T)() : valueOrGetter;
};

type CreateKarteImageListRefresherOptions = {
  debounceMs: ValueOrGetter<number>;
  maxAttempts: ValueOrGetter<number>;
  retryDelayMs: (nextAttempt: number) => number;
  getCurrentReservationId: () => number | null | undefined;
  fetch: (reservationId: number) => Promise<unknown>;
};

type QueueRefreshOptions = {
  reservationId: number;
  onDebouncedStart?: () => void;
  onFinalFailure?: () => void;
};

export type KarteImageListRefresher = {
  queueRefresh: (options: QueueRefreshOptions) => void;
  dispose: () => void;
};

export const createKarteImageListRefresher = (
  options: CreateKarteImageListRefresherOptions
): KarteImageListRefresher => {
  let debounceTimerId: TimerId | null = null;
  let retryTimerId: TimerId | null = null;

  const clearTimers = (): void => {
    if (debounceTimerId != null) {
      clearTimeout(debounceTimerId);
      debounceTimerId = null;
    }
    if (retryTimerId != null) {
      clearTimeout(retryTimerId);
      retryTimerId = null;
    }
  };

  const isStaleReservation = (reservationId: number): boolean => {
    return options.getCurrentReservationId() !== reservationId;
  };

  const refreshWithRetry = async (
    reservationId: number,
    attempt: number,
    onFinalFailure?: () => void
  ): Promise<void> => {
    if (isStaleReservation(reservationId)) return;

    try {
      await options.fetch(reservationId);
    } catch (_error) {
      // in-flight の間に画面遷移して予約が変わった場合は、リトライ/通知を打ち切る
      if (isStaleReservation(reservationId)) return;

      const maxAttempts = Math.max(1, getValue(options.maxAttempts));
      if (attempt < maxAttempts) {
        const nextAttempt = attempt + 1;
        const delayMs = Math.max(0, options.retryDelayMs(nextAttempt));
        if (retryTimerId != null) clearTimeout(retryTimerId);
        retryTimerId = setTimeout(() => {
          void refreshWithRetry(reservationId, nextAttempt, onFinalFailure);
        }, delayMs);
        return;
      }

      onFinalFailure?.();
    }
  };

  const queueRefresh = ({ reservationId, onDebouncedStart, onFinalFailure }: QueueRefreshOptions): void => {
    clearTimers();

    const debounceMs = Math.max(0, getValue(options.debounceMs));
    debounceTimerId = setTimeout(() => {
      onDebouncedStart?.();
      void refreshWithRetry(reservationId, 1, onFinalFailure);
    }, debounceMs);
  };

  return {
    queueRefresh,
    dispose: clearTimers
  };
};
